import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from './moment'
import MonthCalendar from './MonthCalendar'
import DateStepper from './DateStepper'
import { useNavigation, useStartOfDay } from './utils'
import { useSetState } from '@zambezi/sdk/common'

/**
 * Create a React Month Picker component
 *
 * @param {import('./interfaces').MonthPickerProps} props
 * @returns {JSX.Element}
 */
function MonthPicker ({
  isMonthSelected,
  isMonthDisabled,
  isPrevYearDisabled,
  isNextYearDisabled,
  idPrefix,
  withKeyboardNavigation,
  displayedDate,
  defaultYear,
  year: propsYear,
  onMonthSelected,
  onYearChange,
  onFocusChanged
}) {
  const [{ year, focusedMonth }, setState] = useSetState({
    year: propsYear === undefined ? defaultYear : propsYear,
    focusedMonth: displayedDate == null ? null : displayedDate.getMonth()
  })
  const onNavigationRef = useRef()
  const elementRef = useRef()
  const now = useStartOfDay()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()

  useEffect(updateNavigation)
  useEffect(updateYear, [propsYear])
  useNavigation(onNavigationRef, elementRef)

  return (
    <div className='zb-month-picker' aria-live='assertive' ref={elementRef}>
      <section className='zb-month-picker-header'>
        <DateStepper
          date={new Date(year, 0, 1, 0, 0)}
          renderLabel={d => d.getFullYear()}
          onPrevDate={handleYearChange}
          onNextDate={handleYearChange}
          prevDisabled={isPrevYearDisabled(year)}
          nextDisabled={isNextYearDisabled(year)}
        />
      </section>
      <MonthCalendar
        focusedMonth={focusedMonth}
        currentMonth={year === currentYear ? currentMonth : null}
        isMonthSelected={m => isMonthSelected(m, year)}
        isMonthDisabled={m => isMonthDisabled(m, year)}
        onMonthSelected={handleMonthSelected}
        idPrefix={idPrefix}
      />
    </div>
  )

  function updateNavigation () {
    onNavigationRef.current = withKeyboardNavigation && onNavigation
  }

  /**
   * @param {Date} d
   */
  function handleYearChange (d) {
    const year = d.getFullYear()
    if (propsYear === undefined) {
      setState({ year })
    }

    onYearChange(year)
  }

  /**
   * @param {number} month
   */
  function handleMonthSelected (month) {
    onMonthSelected(month, year)
  }

  /**
   * @param {string} keyCode
   * @param {boolean} inside
   */
  function onNavigation (keyCode, inside) {
    const steps = {
      left: -1,
      right: 1,
      up: -3,
      down: 3
    }
    if (keyCode in steps) {
      const month = focusedMonth === null ? 1 : focusedMonth
      const newFocusedDate = moment(displayedDate || now)
        .year(year)
        .month(month)
        .add(steps[keyCode], 'month')
      updateFocusState(
        {
          year: newFocusedDate.year(),
          focusedMonth: newFocusedDate.month()
        },
        newFocusedDate.toDate()
      )
    } else {
      switch (keyCode) {
        case 'enter':
          if (!inside) {
            handleMonthSelected(focusedMonth)
          }
          break
        case 'esc':
          handleMonthSelected(null)
          break
        case 'home':
          updateFocusState({
            focusedMonth: 0
          })
          break
        case 'end':
          updateFocusState({
            focusedMonth: 11
          })
          break
      }
    }
  }

  /**
   * @param {{focusedMonth: number, year?: number}} newState
   * @param {Date=} date
   * @returns {typeof state}
   */
  function updateFocusState (newState, date) {
    if (onFocusChanged) {
      const { focusedMonth: newMonth = focusedMonth } = newState
      onFocusChanged(
        `${idPrefix}-${moment.localeData().monthsShort()[newMonth]}`,
        date
      )
    }
    return setState(newState)
  }

  function updateYear () {
    if (propsYear !== undefined) {
      setState({ year: propsYear })
    }
  }
}

MonthPicker.propTypes = {
  displayedDate: PropTypes.instanceOf(Date),
  defaultYear: PropTypes.number,
  year: PropTypes.number,
  isMonthSelected: PropTypes.func,
  isMonthDisabled: PropTypes.func,
  onYearChange: PropTypes.func,
  onMonthSelected: PropTypes.func,
  isPrevYearDisabled: PropTypes.func,
  isNextYearDisabled: PropTypes.func,
  onFocusChanged: PropTypes.func,
  withKeyboardNavigation: PropTypes.bool,
  idPrefix: PropTypes.string
}

MonthPicker.defaultProps = {
  isMonthSelected: (m, y) => {},
  isMonthDisabled: (m, y) => {},
  onMonthSelected: (m, y) => {},
  onYearChange: y => {},
  isPrevYearDisabled: y => false,
  isNextYearDisabled: y => false,
  withKeyboardNavigation: false
}

export default MonthPicker
